<template>
  <div>
    <div class="slot-header">
      <span>Meio</span>
      <button v-if="$store.state.headgear_mid_item.id" 
              class="btn btn-sm btn-outline-danger reset-btn" 
              @click="resetItem"
              title="Remover item">
        <i class="fas fa-times"></i>
      </button>
    </div>
    <ul class="ul-item-list">
      <li v-for="(item, index) in filteredItems" 
        class="li-item" :key="item.id">
        <span v-if="isNewItem(item.id)" class="new-badge">N</span>
        <img :src="itemIconUrl(item.id)" loading="lazy" @error="onIconError"
          class="img-item list-head-mid-item"
          :class="{
            'item-selected': parseInt(item.id) == $store.state.headgear_mid_item.id,
            'item-disabled': parseInt(item.viewID) == 0,
          }"
          :viewID="item.viewID"
          :title="item.displayName || item.name"
          :item="JSON.stringify(item)"
          @click="clickItem($event)"
        />
      </li>
    </ul>
  </div>
</template>

<script>
import { mapMutations, mapGetters } from "vuex";
import { batchCheckItemsAvailability } from "@/services/divinePrideService";
import { eventBus } from "@/services/eventBus";
import { getTranslatedItemName, queueItemsForTranslation } from "@/services/translationService";
import { itemIconUrl } from "@/env";
import { isNewItem } from "@/config/newItems";

export default {
  name: "ItemListHeadMid",
  props: ["item_filter"],
  computed: {
    ...mapGetters(['getMidItems', 'getServerFilter']),
    filteredItems() {
      // Import blacklist directly to ensure it's always checked
      const blacklist = require('@/config/itemOverrides').default.blacklist;
      
      // Get items with potentially translated names and filter out blacklisted items
      const items = this.getMidItems
        .filter(item => !blacklist.includes(parseInt(item.id))) // Filter out blacklisted items first
        .map(item => {
          // Use translated name if available, otherwise use original name
          const displayName = this.translatedItemNames[item.id] || item.name;
          return { ...item, displayName };
        });
      
      // Filter by name (search in both original and translated names)
      const nameFiltered = items.filter(item => {
        if (!this.item_filter) return true;
        const searchTerm = this.item_filter.toLowerCase().trim();
        // Players trade item IDs in Discord, so a numeric search matches the id
        // as well as the name.
        if (/^\d+$/.test(searchTerm) && String(item.id).includes(searchTerm)) return true;
        return item.name.toLowerCase().includes(searchTerm) ||
               (item.displayName && item.displayName.toLowerCase().includes(searchTerm));
      });
      
      const result =
        this.getServerFilter !== 'LATAM' || this.serverAvailableItems.size === 0
          ? nameFiltered
          : nameFiltered.filter(item => this.serverAvailableItems.has(parseInt(item.id)));

      // New-update items float to the top (stable sort keeps order otherwise).
      return [...result].sort(
        (a, b) => (isNewItem(b.id) ? 1 : 0) - (isNewItem(a.id) ? 1 : 0)
      );
    },
  },
  data() {
    return {
      selected_item: 0,
      serverAvailableItems: new Set(), // Set of item IDs available on the selected server
      isLoadingServerItems: false,
      translatedItemNames: {}, // Cache for translated item names
    };
  },
  watch: {
    // Watch for changes in the server filter and refresh the available items
    getServerFilter: {
      immediate: true,
      handler(newValue) {
        if (newValue === 'LATAM') {
          this.checkServerAvailability();
        }
      }
    },
    // Watch for changes in the mid items list and refresh server availability
    getMidItems: {
      immediate: true,
      handler() {
        if (this.getServerFilter === 'LATAM') {
          this.checkServerAvailability();
        }
      }
    }
  },
  mounted() {
    
    // Load translated item names
    
    // Check server availability if server filter is set to LATAM
    if (this.getServerFilter === 'LATAM') {
      this.checkServerAvailability();
    }
    
    // Listen for refresh events from the server filter toggle
    eventBus.on('refresh-filtered-items', this.handleRefreshItems);
  },
  beforeUnmount() {
    // Clean up event listener
    eventBus.off('refresh-filtered-items', this.handleRefreshItems);
  },
  updated() {
  },
  methods: {
    itemIconUrl, // exposed to template (Options API can't see module imports)
    isNewItem,   // exposed to template for the "N" badge
    onIconError(e) {
      if (e && e.target) e.target.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    },
    ...mapMutations([
      "SAVE_HEADGEAR_TOP",
      "SAVE_HEADGEAR_MID",
      "SAVE_HEADGEAR_MID_ITEM",
      "SAVE_HEADGEAR_TOP",
      "SAVE_HEADGEAR_TOP_ITEM",
      "SAVE_HEADGEAR_BOTTOM",
      "SAVE_HEADGEAR_BOTTOM_ITEM",
      "SAVE_GARMENT",
      "SAVE_GARMENT_ITEM",
    ]),
    
    /**
     * Load translated item names from the centralized translation service
     */
    async loadTranslatedItemNames() {
      try {
        const items = this.getMidItems;
        
        // Check if we already have translations in cache
        let hasAllTranslations = true;
        items.forEach(item => {
          const translatedName = getTranslatedItemName(item.id);
          if (translatedName) {
            // Update our local cache from the global cache
            this.translatedItemNames[item.id] = translatedName;
          } else {
            hasAllTranslations = false;
          }
        });
        
        if (hasAllTranslations) {
          this.$nextTick(() => {
          });
          return;
        }
        
        // Queue items for translation
        const language = this.$store.state.ui.language;
        
        queueItemsForTranslation(items, language, () => {
          
          // Update our local cache from the global cache
          let updatedCount = 0;
          items.forEach(item => {
            const translatedName = getTranslatedItemName(item.id);
            if (translatedName) {
              this.translatedItemNames[item.id] = translatedName;
              updatedCount++;
            }
          });
          
          
          // Force tooltip update after translations are loaded
          this.$nextTick(() => {
          });
        });
      } catch (error) {
      }
    },
    
    /**
     * Check which items are available on the selected server
     */
    async checkServerAvailability() {
      try {
        // Set loading state
        this.isLoadingServerItems = true;
        // Emit loading state change
        eventBus.emit('server-items-loading', true);
        
        // Get all item IDs
        const itemIds = this.getMidItems.map(item => parseInt(item.id));
        
        if (itemIds.length === 0) {
          this.serverAvailableItems = new Set();
          this.isLoadingServerItems = false;
          eventBus.emit('server-items-loading', false);
          return;
        }
        
        // Check availability in batches
        const availabilityMap = await batchCheckItemsAvailability(itemIds, 'LATAM');
        
        // Update the set of available items
        this.serverAvailableItems = new Set();
        availabilityMap.forEach((isAvailable, itemId) => {
          if (isAvailable) {
            this.serverAvailableItems.add(itemId);
          }
        });
        
      } catch (error) {
      } finally {
        this.isLoadingServerItems = false;
        // Emit loading state change
        eventBus.emit('server-items-loading', false);
      }
    },
    clickItem: function (event) {
      this.SAVE_HEADGEAR_MID(parseInt(event.target.getAttribute("viewID")));
      this.validMid();
    },
    
    /**
     * Reset the mid headgear item
     */
    resetItem() {
      // Create an empty item
      const emptyItem = { viewID: 0, id: 0 };
      
      // Update the store
      this.SAVE_HEADGEAR_MID(0);
      this.SAVE_HEADGEAR_MID_ITEM(emptyItem);
      
      // Update tooltips
    },
    validMid: function () {
      const item_fortmat = JSON.parse(event.target.getAttribute("item"));
      // validar interseções
      const state_fortmat =  JSON.parse(JSON.stringify(this.$store.state));

      const database = []
      database.push(state_fortmat.headgear_top_item);
      database.push(state_fortmat.headgear_mid_item);
      database.push(state_fortmat.headgear_bottom_item);
      database.push(state_fortmat.garment_item);
      let novoItem = item_fortmat;

      const itensParaRemover = database.filter(item => (
        (item?.top && novoItem.top) 
        || (item?.mid && novoItem.mid) 
        || (item?.bot && novoItem.bot) 
        || (item?.garment && novoItem.garment)
      ));

      // remove caso estjá vazio 
      const new_dados = database.filter((item) => Object.keys(item).length != 0)
      // Remover os itens filtrados
      itensParaRemover.forEach(item => {
        const index = database.indexOf(item);
        if (index !== -1) {
          database.splice(index, 1);
        }
      });

      const remover = itensParaRemover.filter((item) => item.id != novoItem.id)

      database.push(novoItem);

      // remover
      for (let index = 0; index < remover.length; index++) {
        const item = remover[index];
        const remove_item = {viewID: 0}

        if (item.top) {
          this.updateHeadgearTop(remove_item);
        }
        if (item.bot) {    
          this.updateHeadgearBot(remove_item);
        }
        if (item.garment) {
          this.updateGarment(remove_item);
        }
      }

      const item_types = [];

      if (item_fortmat.top) {
        item_types.push("TOP");
        this.updateHeadgearTop(item_fortmat);
      }
      if (item_fortmat.mid) {
        item_types.push("MID");
      }
      if (item_fortmat.bot) {
        item_types.push("BOT");
        this.updateHeadgearBot(item_fortmat);
      }
      if (item_fortmat.garment) {
        item_types.push("GARMENT");
        this.updateGarment(item_fortmat);
      }

      item_fortmat.location = item_types;
      this.SAVE_HEADGEAR_MID_ITEM(item_fortmat);
    },
    updateHeadgearTop: function (item) {
      this.SAVE_HEADGEAR_TOP(item.viewID);
      this.SAVE_HEADGEAR_TOP_ITEM(item);
    },
    updateHeadgearMid: function (item) {
      this.SAVE_HEADGEAR_MID(item.viewID);
      this.SAVE_HEADGEAR_MID_ITEM(item);
    },
    updateHeadgearBot: function (item) {
      this.SAVE_HEADGEAR_BOTTOM(item.viewID);
      this.SAVE_HEADGEAR_BOTTOM_ITEM(item);
    },
    updateGarment: function (item) {
      this.SAVE_GARMENT(item.viewID);
      this.SAVE_GARMENT_ITEM(item);
    },
    
    handleRefreshItems() {
      // Re-check server availability if needed
      if (this.getServerFilter === 'LATAM') {
        this.checkServerAvailability();
      } else {
        // Force computed property recalculation
        this.$forceUpdate();
      }
    },
  },
};
</script>

<style scoped>
.slot-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
  padding: 0 1rem;
}

.reset-btn {
  padding: 0 5px;
  font-size: 0.7rem;
  line-height: 1.2;
  height: 20px;
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ul-item-list {
  list-style: none;
  padding-top: 2px;
  padding-left: 1rem;
  clear: both;
}

.li-item {
  position: relative;
  float: left;
  width: 32px;
  height: 32px;
  background: url("~@/assets/img/interface/item-bg.png");
}

.new-badge {
  position: absolute;
  top: -3px;
  right: -3px;
  z-index: 2;
  width: 14px;
  height: 14px;
  line-height: 14px;
  text-align: center;
  font-size: 9px;
  font-weight: bold;
  color: #fff;
  background: #e53935;
  border-radius: 50%;
  box-shadow: 0 0 0 1px #fff;
  pointer-events: none;
}

.img-item {
  width: 24px;
  height: 24px;
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.item-selected {
  outline: 2px dashed #0d6efd;
  border-radius: 5px;
  filter: contrast(105%) brightness(110%);
}

.item-disabled {
  opacity: 0.5;
  background: url("~@/assets/img/interface/forbidden_item.png");
}

</style>
